import React, { useEffect, useState } from "react";

type NumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> & {
  value: number | null | undefined;
  /** Receives the parsed number; an empty or partial entry reports `emptyValue`. */
  onValueChange: (value: number) => void;
  /** Reported while the field is empty or mid-entry (e.g. "0." or "-"). */
  emptyValue?: number;
};

const toText = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? "" : String(value);

const toNumber = (text: string, emptyValue: number): number => {
  const parsed = Number(text);
  return text.trim() === "" || !Number.isFinite(parsed) ? emptyValue : parsed;
};

/**
 * Numeric input that keeps the raw text the user typed.
 *
 * Binding a number straight to `<input type="number">` makes some values
 * impossible to type: "0" round-trips back as an empty field, and a partial
 * decimal ("0.") is reported by the browser as "" and gets overwritten on the
 * next render, so "0.6" ends up as "6". Holding the text locally and reporting
 * only the parsed value upwards keeps every keystroke intact while the parent
 * still works with numbers. The spinner arrows keep working because the field
 * remains a number input.
 */
const NumberInput = ({ value, onValueChange, emptyValue = 0, ...inputProps }: NumberInputProps) => {
  const [text, setText] = useState<string>(() => toText(value));

  // Follow changes coming from the parent (row reset, reloaded record) without
  // rewriting text that already represents the current value.
  useEffect(() => {
    setText((current) => (toNumber(current, emptyValue) === value ? current : toText(value)));
  }, [value, emptyValue]);

  return (
    <input
      {...inputProps}
      type="number"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onValueChange(toNumber(e.target.value, emptyValue));
      }}
      onFocus={(e) => {
        // A field still holding its default (usually a fresh row's 0) behaves
        // like a placeholder: select it so typing replaces it instead of
        // appending, which would read as "05".
        if (toNumber(e.target.value, emptyValue) === emptyValue) e.target.select();
        inputProps.onFocus?.(e);
      }}
      onBlur={(e) => {
        // Drop leftovers of partial entry ("0.", "05") once the field is left.
        setText(toText(toNumber(e.target.value, emptyValue)));
        inputProps.onBlur?.(e);
      }}
    />
  );
};

export default NumberInput;
